import { useTranslation } from 'react-i18next';
import ServiceLayout from './components/ServiceLayout';
import useSeoPage from '@/hooks/useSeoPage';

export default function PreventionPage() {
  const { t } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL ?? '';

  useSeoPage({
    title: 'Профилактика и гигиена полости рта Кишинёв | Чистка зубов | Zimbet32',
    description: 'Профессиональная чистка зубов, отбеливание и профилактика кариеса в Кишинёве. Zimbet32 — igienă dentară profesională și prevenție în Chișinău.',
    keywords: 'профессиональная чистка зубов Кишинёв, отбеливание зубов, профилактика кариеса, фторирование, igienă dentară Chișinău, Zimbet32',
    canonicalPath: '/services/prevention',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'MedicalProcedure',
      name: 'Профилактика и гигиена полости рта',
      url: `${siteUrl}/services/prevention`,
      description: 'Профессиональная гигиена полости рта: чистка зубов, отбеливание, фторирование, герметизация фиссур в Кишинёве.',
      procedureType: 'https://schema.org/PreventiveProcedure',
      bodyLocation: 'Зубы, дёсны',
      provider: {
        '@type': 'Dentist',
        name: 'Zimbet32',
        url: siteUrl,
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Strada București 60',
          addressLocality: 'Chișinău',
          postalCode: 'MD-2012',
          addressCountry: 'MD',
        },
        telephone: '+373-22-22-07-09',
      },
    },
  });

  const procedures = [
    {
      icon: 'ri-brush-line',
      title: t('servicePages.prevention.procedures.cleaning.title'),
      description: t('servicePages.prevention.procedures.cleaning.description')
    },
    {
      icon: 'ri-flashlight-line',
      title: t('servicePages.prevention.procedures.whitening.title'),
      description: t('servicePages.prevention.procedures.whitening.description')
    },
    {
      icon: 'ri-shield-check-line',
      title: t('servicePages.prevention.procedures.fluoride.title'),
      description: t('servicePages.prevention.procedures.fluoride.description')
    },
    {
      icon: 'ri-search-eye-line',
      title: t('servicePages.prevention.procedures.checkup.title'),
      description: t('servicePages.prevention.procedures.checkup.description')
    },
    {
      icon: 'ri-drop-line',
      title: t('servicePages.prevention.procedures.sealants.title'),
      description: t('servicePages.prevention.procedures.sealants.description')
    },
    {
      icon: 'ri-book-open-line',
      title: t('servicePages.prevention.procedures.education.title'),
      description: t('servicePages.prevention.procedures.education.description')
    }
  ];

  const advantages = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: t('servicePages.prevention.benefits.savings.title'),
      description: t('servicePages.prevention.benefits.savings.description')
    },
    {
      icon: 'ri-heart-add-line',
      title: t('servicePages.prevention.benefits.health.title'),
      description: t('servicePages.prevention.benefits.health.description')
    },
    {
      icon: 'ri-time-line',
      title: t('servicePages.prevention.benefits.early.title'),
      description: t('servicePages.prevention.benefits.early.description')
    }
  ];

  const galleryImages = [
    'https://public.readdy.ai/ai/img_res/a814813aef3cc3ee89348455cae4baf0.jpg',
    'https://readdy.ai/api/search-image?query=teeth%20whitening%20procedure%20with%20professional%20bleaching%20gel%20and%20LED%20light%20activation%20in%20modern%20dental%20office%20showing%20bright%20white%20smile%20results%20with%20clean%20background%20and%20professional%20lighting&width=800&height=600&seq=prevention-whitening-2&orientation=landscape',
    'https://readdy.ai/api/search-image?query=dentist%20applying%20fluoride%20treatment%20varnish%20on%20teeth%20for%20cavity%20prevention%20in%20bright%20modern%20clinic%20with%20professional%20dental%20instruments%20and%20clean%20white%20medical%20environment%20showing%20preventive%20care&width=800&height=600&seq=prevention-fluoride-3&orientation=landscape',
    'https://readdy.ai/api/search-image?query=dental%20checkup%20examination%20with%20dentist%20using%20mirror%20and%20explorer%20tools%20inspecting%20teeth%20in%20modern%20clinic%20with%20bright%20professional%20lighting%20and%20clean%20white%20background%20showing%20routine%20preventive%20care&width=800&height=600&seq=prevention-checkup-4&orientation=landscape',
    'https://readdy.ai/api/search-image?query=dental%20sealant%20application%20on%20molar%20teeth%20for%20cavity%20prevention%20showing%20protective%20coating%20being%20applied%20in%20modern%20dental%20clinic%20with%20professional%20equipment%20and%20clean%20white%20medical%20environment&width=800&height=600&seq=prevention-sealant-5&orientation=landscape',
    'https://readdy.ai/api/search-image?query=beautiful%20healthy%20white%20teeth%20and%20pink%20gums%20after%20professional%20dental%20hygiene%20treatment%20showing%20perfect%20oral%20health%20in%20bright%20modern%20dental%20office%20with%20professional%20photography%20lighting%20and%20clean%20background&width=800&height=600&seq=prevention-result-6&orientation=landscape'
  ];

  return (
    <ServiceLayout
      heroImage="https://readdy.ai/api/search-image?query=modern%20dental%20hygiene%20room%20with%20professional%20cleaning%20equipment%20ultrasonic%20scalers%20and%20polishing%20tools%20in%20bright%20clean%20white%20medical%20environment%20showing%20preventive%20dentistry%20workspace%20with%20organized%20instruments&width=1920&height=600&seq=prevention-hero&orientation=landscape"
      breadcrumbKey="servicePages.prevention.title"
      titleKey="servicePages.prevention.title"
      descriptionKey="servicePages.prevention.description"
      procedures={procedures}
      advantages={advantages}
      galleryImages={galleryImages}
      ctaTitleKey="servicePages.prevention.ctaTitle"
      ctaDescriptionKey="servicePages.prevention.ctaDescription"
      ctaButtonKey="servicePages.prevention.ctaButton"
    />
  );
}
